import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DataTestDirective, TargetUrl } from '@outlaw/core';
import { TruncatePipe } from '../../../../pipes/truncate.pipe';
import { FormatLineBreaksPipe } from '../../../../pipes/format-line-breaks.pipe';

@Component({
  selector: 'app-text-content',
  imports: [NgTemplateOutlet, TruncatePipe, FormatLineBreaksPipe, DataTestDirective],
  templateUrl: './text-content.component.html',
  styleUrls: ['./text-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextContentComponent {
  title = input<string>('');
  text = input<string>('');
  postUrl = input<string>();
  maxTextLength = input<number>(undefined);
  requirePurchase = input<boolean>();
  openInSameWindow = input<boolean>(false);

  postClick = output<void>();

  descriptionElement = viewChild<ElementRef<HTMLElement>>('descriptionElement');

  isExpanded = signal(false);
  isAnimating = signal(false);

  private readonly ANIMATION_DURATION = 300;

  linkTarget = computed(() => (this.openInSameWindow() ? TargetUrl.sameWindow : TargetUrl.newWindow));

  shouldShowMoreButton = computed(() => {
    const text = this.text();
    return !this.isExpanded() && text && this.maxTextLength() && text.length > this.maxTextLength();
  });

  maxLength = computed(() => (this.isExpanded() ? undefined : this.maxTextLength()));

  onPostClick(event: Event): void {
    if (this.openInSameWindow()) {
      event.preventDefault();
      this.postClick.emit();
    }
  }

  expandText(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const elementRef = this.descriptionElement();
    if (elementRef) {
      this.animateExpansion(elementRef.nativeElement);
    } else {
      this.isExpanded.set(true);
    }
  }

  private animateExpansion(element: HTMLElement): void {
    const currentHeight = element.scrollHeight;

    element.style.height = `${currentHeight}px`;
    element.style.maxHeight = `${currentHeight}px`;

    this.isAnimating.set(true);
    this.isExpanded.set(true);

    requestAnimationFrame(() => {
      const newHeight = element.scrollHeight;
      element.style.height = `${newHeight}px`;
      element.style.maxHeight = `${newHeight}px`;

      setTimeout(() => {
        element.style.height = 'auto';
        element.style.maxHeight = 'none';
        this.isAnimating.set(false);
      }, this.ANIMATION_DURATION);
    });
  }
}
